import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, ChevronLeft, ChevronRight, Users, TrendingUp, Bell, Sparkles,
  Play, Compass, Layers, Menu, Radio,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import NexusLogo from "@/components/NexusLogo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function formatViewers(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

/* ── Left sidebar (Canais ao vivo) ───────────────────────────────── */
function LiveChannelsSidebar({ streams, onOpen, collapsed, setCollapsed }) {
  return (
    <aside
      className={`shrink-0 border-r border-[hsl(var(--nx-border))] bg-[hsl(var(--nx-panel))] overflow-y-auto scrollbar-thin transition-[width] duration-200
      ${collapsed ? "w-14" : "w-64"}`}
      data-testid="public-live-sidebar"
    >
      <div className="p-3 flex items-center justify-between border-b border-[hsl(var(--nx-border))] sticky top-0 bg-[hsl(var(--nx-panel))] z-10">
        {!collapsed && (
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
            <TrendingUp className="w-3.5 h-3.5" /> Canais ao vivo
          </div>
        )}
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="w-7 h-7 rounded-md hover:bg-[hsl(var(--nx-panel-3))] grid place-items-center text-muted-foreground"
          data-testid="live-sidebar-toggle"
          title={collapsed ? "Expandir" : "Recolher"}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      <ul className={collapsed ? "p-1.5 space-y-1" : "p-2 space-y-0.5"}>
        {streams.map((s) => (
          <li key={s.id}>
            <button
              onClick={() => onOpen(s)}
              data-testid={`live-sidebar-${s.id}`}
              className={`w-full text-left flex items-center gap-2.5 rounded-md hover:bg-[hsl(var(--nx-panel-3))]/60 transition-colors
              ${collapsed ? "p-1 justify-center" : "p-2"}`}
              title={s.creator}
            >
              <div className="relative shrink-0">
                <img
                  src={`https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(s.creator)}&backgroundColor=1d2029,242b3a,3d2650`}
                  alt=""
                  className={`rounded-full ${collapsed ? "w-8 h-8" : "w-8 h-8"}`}
                />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[hsl(var(--nx-red))] ring-2 ring-[hsl(var(--nx-panel))]" />
              </div>
              {!collapsed && (
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.creator}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{s.game}</div>
                </div>
              )}
              {!collapsed && (
                <div className="flex items-center gap-1 text-[10px] text-[hsl(var(--nx-red))] font-mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--nx-red))]" />
                  {formatViewers(s.viewers)}
                </div>
              )}
            </button>
          </li>
        ))}
      </ul>

      {!collapsed && (
        <div className="p-3 mt-2 border-t border-[hsl(var(--nx-border))]">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
            <Compass className="w-3 h-3" /> Categorias populares
          </div>
          <ul className="space-y-1">
            {["Valorant", "Só Na Conversa", "Music", "Cyberpunk 2077", "Programação"].map((c) => (
              <li key={c} className="text-xs text-muted-foreground hover:text-foreground cursor-pointer px-1.5 py-1 rounded-md hover:bg-[hsl(var(--nx-panel-3))]/60">
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}

/* ── Public top nav ─────────────────────────────────────────────── */
function PublicTopBar({ user }) {
  const nav = useNavigate();
  return (
    <header className="h-16 shrink-0 border-b border-[hsl(var(--nx-border))] bg-[hsl(var(--nx-panel))]/70 backdrop-blur px-4 lg:px-6 flex items-center gap-4 z-30 relative">
      <NexusLogo to="/" size={30} />
      <div className="hidden lg:block h-6 w-px bg-[hsl(var(--nx-border))]" />

      <nav className="hidden lg:flex items-center gap-1">
        {[
          { l: "Procurar", to: "/", icon: Compass },
          { l: "Categorias", to: "/#categorias", icon: Layers },
          { l: "Seguindo", to: user ? "/app" : "/login", icon: Users },
        ].map((n) => (
          <button
            key={n.l}
            onClick={() => nav(n.to)}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--nx-panel-3))]/60 transition-colors"
            data-testid={`public-nav-${n.l.toLowerCase()}`}
          >
            <n.icon className="w-4 h-4" /> {n.l}
          </button>
        ))}
      </nav>

      <div className="flex-1 max-w-lg mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            placeholder="Buscar streamers, categorias ou canais…"
            data-testid="public-search"
            className="w-full h-10 pl-10 pr-4 bg-[hsl(var(--nx-panel-2))] border border-[hsl(var(--nx-border))] rounded-xl text-sm placeholder:text-muted-foreground focus:outline-none focus:border-[hsl(var(--nx-blue))]"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button className="w-9 h-9 rounded-lg bg-[hsl(var(--nx-panel-2))] hover:bg-[hsl(var(--nx-panel-3))] grid place-items-center transition-colors" data-testid="public-topbar-notifications" title="Notificações">
          <Bell className="w-4 h-4" />
        </button>
        {user ? (
          <Link to="/app" data-testid="public-open-app" className="inline-flex items-center gap-2 h-9 px-4 rounded-lg nx-btn-blue border-0 text-sm font-semibold">
            Meu painel
          </Link>
        ) : (
          <>
            <Link to="/login">
              <Button variant="ghost" data-testid="public-login-btn" className="rounded-lg hover:bg-white/5 h-9">Entrar</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="public-register-btn" className="rounded-lg nx-btn-blue border-0 px-4 h-9 font-semibold">
                Cadastrar-se
              </Button>
            </Link>
          </>
        )}
      </div>
    </header>
  );
}

/* ── Hero rotator ───────────────────────────────────────────────── */
function HeroCarousel({ items, onOpen }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!items.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);

  const s = items[idx];
  if (!s) return null;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-[hsl(var(--nx-border))] group nx-glow-blue">
      <div className="aspect-[16/8] relative">
        <img src={s.thumbnail} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className="nx-chip nx-chip-red !py-1"><span className="nx-live-dot" style={{background:'#fff'}} /> AO VIVO</span>
          <span className="nx-chip !py-1"><Users className="w-3 h-3" /> {s.viewers.toLocaleString("pt-BR")}</span>
        </div>

        <button
          onClick={() => onOpen(s)}
          className="absolute inset-0 grid place-items-center"
          data-testid="hero-play-btn"
          aria-label={`Assistir ${s.title}`}
        >
          <span className="w-20 h-20 rounded-full bg-white/10 backdrop-blur border border-white/25 grid place-items-center opacity-90 group-hover:scale-105 transition-transform">
            <Play className="w-9 h-9 text-white ml-1" />
          </span>
        </button>

        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
          <div className="min-w-0 max-w-2xl">
            <div className="text-[11px] uppercase tracking-widest text-white/70 font-semibold mb-1">Destaque · {s.game}</div>
            <h2 className="font-display font-bold text-white text-2xl sm:text-3xl md:text-4xl leading-tight line-clamp-2">{s.title}</h2>
            <div className="text-white/80 text-sm mt-1">por <span className="text-white font-semibold">@{s.creator}</span></div>
          </div>
          <div className="flex items-center gap-1.5">
            {items.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-white" : "w-3 bg-white/40 hover:bg-white/70"}`}
                aria-label={`slide ${i+1}`}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Stream card ────────────────────────────────────────────────── */
function StreamCard({ s, onOpen }) {
  return (
    <button
      onClick={() => onOpen(s)}
      data-testid={`public-stream-${s.id}`}
      className="group block text-left nx-panel overflow-hidden hover:border-[hsl(var(--nx-blue))] transition-colors nx-fade-up"
    >
      <div className="relative aspect-video overflow-hidden">
        <img src={s.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <span className="absolute top-2.5 left-2.5 nx-chip nx-chip-red !py-1 !px-2"><span className="nx-live-dot" style={{background:'#fff'}} /> AO VIVO</span>
        <span className="absolute top-2.5 right-2.5 nx-chip !py-1 !px-2"><Users className="w-3 h-3" /> {s.viewers.toLocaleString("pt-BR")}</span>
      </div>
      <div className="p-3 flex gap-3">
        <img
          src={`https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(s.creator)}&backgroundColor=1d2029,242b3a,3d2650`}
          alt=""
          className="w-9 h-9 rounded-full ring-2 ring-[hsl(var(--nx-border))]"
        />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate">{s.title}</div>
          <div className="text-[11px] text-muted-foreground truncate">@{s.creator}</div>
          <div className="text-[11px] text-muted-foreground truncate">{s.game}</div>
        </div>
      </div>
    </button>
  );
}

export default function PublicHome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [streams, setStreams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    api.get("/discover/streams").then((r) => setStreams(r.data)).catch(() => {});
    api.get("/discover/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const featured = useMemo(() => streams.slice(0, 3), [streams]);
  const otherStreams = useMemo(() => streams.slice(0, 8), [streams]);

  const openStream = (s) => nav(`/live/${s.id}`);

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      <PublicTopBar user={user} />

      <div className="flex-1 min-h-0 flex overflow-hidden">
        <LiveChannelsSidebar streams={streams.slice(0, 12)} onOpen={openStream} collapsed={collapsed} setCollapsed={setCollapsed} />

        <main className="flex-1 min-w-0 overflow-y-auto scrollbar-thin" data-testid="public-home">
          <div className="p-6 lg:p-8 space-y-10 max-w-[1400px] mx-auto">

            {/* HERO */}
            {featured.length > 0 && (
              <section>
                <HeroCarousel items={featured} onOpen={openStream} />
              </section>
            )}

            {/* Live now */}
            <section id="ao-vivo">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="nx-chip nx-chip-red mb-2"><span className="nx-live-dot" /> Agora</div>
                  <h2 className="font-display font-bold text-2xl">Ao vivo na Nexus Live</h2>
                </div>
                <Link to="/app/explorar" className="text-sm text-[hsl(var(--nx-cyan))] hover:underline">
                  ver todas →
                </Link>
              </div>
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {otherStreams.map((s) => <StreamCard key={s.id} s={s} onOpen={openStream} />)}
              </div>
            </section>

            {/* Categorias */}
            <section id="categorias">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <div className="nx-chip nx-chip-cyan mb-2"><Layers className="w-3.5 h-3.5" /> Categorias</div>
                  <h2 className="font-display font-bold text-2xl">Populares agora</h2>
                </div>
              </div>
              <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
                {categories.map((c) => (
                  <div key={c.id} data-testid={`public-category-${c.id}`}
                    onClick={() => nav("/app/explorar")}
                    className="group nx-panel overflow-hidden hover:border-[hsl(var(--nx-cyan))] transition-colors cursor-pointer">
                    <div className="aspect-[3/4] overflow-hidden relative">
                      <img src={c.cover} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <div className="absolute bottom-2 left-2 right-2">
                        <div className="text-white text-sm font-semibold truncate">{c.name}</div>
                        <div className="text-white/70 text-[10px]">{c.viewers.toLocaleString("pt-BR")} assistindo</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Sticky footer CTA para não-logados */}
            {!user && (
              <section className="pt-4">
                <div className="nx-glass rounded-3xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between relative overflow-hidden">
                  <div className="absolute -top-16 -right-10 w-[280px] h-[280px] rounded-full bg-[hsl(var(--nx-blue)/0.22)] blur-[100px]" />
                  <div className="relative">
                    <div className="nx-chip nx-chip-blue mb-2"><Sparkles className="w-3.5 h-3.5" /> Junte-se</div>
                    <h3 className="font-display font-bold text-xl sm:text-2xl">Participe da comunidade Nexus Live!</h3>
                    <p className="text-sm text-muted-foreground mt-1">Descubra transmissões, entre em comunidades e converse com criadores.</p>
                  </div>
                  <Link to="/register" data-testid="public-cta-register" className="relative inline-flex items-center gap-2 h-11 px-6 rounded-xl nx-btn-blue border-0 text-sm font-semibold shrink-0">
                    Cadastrar-se
                  </Link>
                </div>
              </section>
            )}

            <footer className="pt-6 pb-2 border-t border-[hsl(var(--nx-border))] mt-2 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-muted-foreground">
              <div className="flex items-center gap-2"><NexusLogo size={20} withText={false} /> © {new Date().getFullYear()} Nexus Live</div>
              <div className="flex items-center gap-4">
                <Link to="/apresentacao" className="hover:text-foreground" data-testid="public-about-link">Sobre a plataforma</Link>
                <span>Termos</span>
                <span>Privacidade</span>
                <span className="font-mono">v1.0</span>
              </div>
            </footer>
          </div>
        </main>
      </div>
    </div>
  );
}
